/**
 * Serviço de Organizações
 * Responsabilidade: Gerenciar operações de organizações, membros e convites
 */

import { supabase } from '../../lib/supabase';
import { handleServiceError } from '../utils/serviceUtils';

export interface OrganizationInvite {
    id: string;
    email: string;
    role: 'admin' | 'member' | 'viewer';
    status: 'pending' | 'accepted' | 'expired' | 'revoked';
    created_at: string;
    expires_at: string;
    token?: string;
}

export interface OrganizationMember {
    id: string;
    user_id: string;
    role: 'owner' | 'admin' | 'member' | 'viewer';
    joined_at: string;
    user?: {
        email: string;
        full_name?: string;
    };
}

/**
 * Busca convites pendentes da organização
 */
export const getPendingInvites = async (organizationId: string): Promise<OrganizationInvite[]> => {
    try {
        const { data, error } = await supabase
            .from('organization_invites')
            .select('id, email, role, status, created_at, expires_at, token')
            .eq('organization_id', organizationId)
            .eq('status', 'pending');

        if (error) throw error;
        return data || [];
    } catch (error) {
        return handleServiceError('buscar convites', error) || [];
    }
};

export interface InviteDetails {
    email: string;
    organization_name: string;
    organization_id?: string;
    role?: string;
}

const INVITE_SESSION_KEY = 'pending_invite_token';

export function getPendingInviteToken(): string | null {
    const params = new URLSearchParams(window.location.search);
    const fromUrl = params.get('token') || params.get('invite');
    if (fromUrl?.trim()) return fromUrl.trim();
    return sessionStorage.getItem(INVITE_SESSION_KEY);
}

export function persistInviteToken(token: string): void {
    sessionStorage.setItem(INVITE_SESSION_KEY, token);
}

export function clearInviteToken(): void {
    sessionStorage.removeItem(INVITE_SESSION_KEY);
}

export function buildInvitePath(token: string): string {
    return `/convite?token=${encodeURIComponent(token)}`;
}

export function buildInviteLink(token: string): string {
    return `${window.location.origin}${buildInvitePath(token)}`;
}

export function isInviteRoute(): boolean {
    return window.location.pathname.replace(/\/$/, '') === '/convite';
}

export function redirectLegacyInviteUrl(): void {
    const legacy = new URLSearchParams(window.location.search).get('invite');
    if (!legacy) return;
    const path = window.location.pathname.replace(/\/$/, '');
    if (path === '/register' || path === '/registro' || path === '/login') {
        persistInviteToken(legacy);
        window.history.replaceState(null, '', buildInvitePath(legacy));
    }
}

/**
 * Busca detalhes públicos do convite (pré-autenticação).
 */
export const getInviteDetails = async (inviteToken: string): Promise<InviteDetails | null> => {
    try {
        const baseUrl = import.meta.env.VITE_SUPABASE_URL;
        const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
        const res = await fetch(`${baseUrl}/functions/v1/get-invite-details`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${anonKey}`,
                apikey: anonKey,
            },
            body: JSON.stringify({ invite_token: inviteToken }),
        });

        const payload = await res.json().catch(() => ({}));
        if (!res.ok || payload.error) return null;

        const rows = payload.data as unknown;
        const details = Array.isArray(rows) ? rows[0] : rows;
        return details ?? null;
    } catch {
        return null;
    }
};

async function runInviteRpc(
    rpc: 'reshare_organization_invite' | 'regenerate_organization_invite',
    inviteId: string
): Promise<{ token: string; expiresAt: string }> {
    const { data, error } = await supabase.rpc(rpc, { p_invite_id: inviteId });
    if (error) throw error;
    const row = (data ?? {}) as Record<string, unknown>;
    if (!row.success) throw new Error(String(row.error ?? 'Erro ao processar convite'));
    return { token: String(row.token), expiresAt: String(row.expires_at) };
}

/** Envia convite e retorna o link compartilhável (/convite?token=...). */
export const sendInvite = async (
    organizationId: string,
    email: string,
    role: string
): Promise<string> => {
    try {
        const token = crypto.randomUUID();
        const expiresAt = new Date();
        expiresAt.setDate(expiresAt.getDate() + 7);

        const { data: { user } } = await supabase.auth.getUser();

        const { data, error } = await supabase
            .from('organization_invites')
            .insert({
                organization_id: organizationId,
                email,
                role,
                token,
                status: 'pending',
                expires_at: expiresAt.toISOString(),
                invited_by: user?.id ?? null,
            })
            .select('id, token, expires_at')
            .single();

        if (error) throw error;

        return buildInviteLink(data.token ?? token);
    } catch (error) {
        handleServiceError('enviar convite', error);
        throw error;
    }
};

export const reshareInvite = (inviteId: string) =>
    runInviteRpc('reshare_organization_invite', inviteId).catch(err => {
        handleServiceError('reenviar convite', err);
        throw err;
    });

export const regenerateInvite = (inviteId: string) =>
    runInviteRpc('regenerate_organization_invite', inviteId).catch(err => {
        handleServiceError('regenerar convite', err);
        throw err;
    });

/**
 * Revoga um convite
 */
export const revokeInvite = async (inviteId: string): Promise<void> => {
    try {
        const { error } = await supabase
            .from('organization_invites')
            .delete() // User requested hard delete ("excluir")
            .eq('id', inviteId);

        if (error) throw error;
    } catch (error) {
        handleServiceError('revogar convite', error);
        throw error; // Rethrow to allow hook to catch and handle UI
    }
};

/**
 * Busca membros da organização
 */
export const getOrganizationMembers = async (organizationId: string): Promise<OrganizationMember[]> => {
    try {
        const { data: members, error } = await supabase
            .rpc('get_org_members', { p_org_id: organizationId });

        if (error) throw error;

        if (!members || members.length === 0) {
            return [];
        }

        // Mapear resultado da RPC para o formato OrganizationMember
        return members.map((member: any) => ({
            id: member.id,
            user_id: member.user_id,
            role: member.role,
            joined_at: null,
            user: {
                email: member.email || 'Email desconhecido',
                full_name: member.full_name
            }
        }));

    } catch (error) {
        console.error('[getOrganizationMembers] Error:', error);
        return handleServiceError('buscar membros', error) || [];
    }
};

/**
 * Aceita um convite (para usuário já autenticado)
 */
export const acceptInvite = async (token: string): Promise<any> => {
    try {
        const { data, error } = await supabase.rpc('accept_invite', { invite_token: token });
        if (error) throw error;
        return data;
    } catch (error) {
        handleServiceError('aceitar convite', error);
        throw error;
    }
};

/**
 * Atualiza a role de um membro via RPC SECURITY DEFINER
 * (UPDATE direto sob RLS falhava em silêncio: SELECT só via própria membership).
 */
export const updateMemberRole = async (memberId: string, role: 'admin' | 'member' | 'viewer'): Promise<void> => {
    try {
        const normalizedRole = role === 'viewer' ? 'member' : role;
        const { data, error } = await supabase.rpc('update_org_member_role', {
            p_member_id: memberId,
            p_role: normalizedRole,
        });

        if (error) throw error;
        if (!data?.success) {
            throw new Error(String(data?.error || 'Erro ao atualizar função do membro'));
        }
    } catch (error) {
        handleServiceError('atualizar função do membro', error);
        throw error;
    }
};

/**
 * Remove um membro da organização via RPC SECURITY DEFINER.
 * Toda a autorização (verificar se é owner) e desatribuições
 * são feitas dentro da função SQL — sem depender de policy RLS de DELETE.
 */
export const removeMember = async (memberId: string, _userId: string): Promise<void> => {
    try {
        const { data, error } = await supabase.rpc('remove_org_member', {
            p_member_id: memberId
        });

        if (error) throw error;

        if (!data?.success) {
            const message = String(data?.error || 'Erro ao remover membro');
            if (message.toLowerCase().includes('membro não encontrado')) return;
            throw new Error(message);
        }
    } catch (error) {
        console.error('[removeMember] Erro:', error);
        handleServiceError('remover membro', error);
        throw error;
    }
};

/**
 * Envia email para redefinição de senha do membro
 */
export const sendPasswordReset = async (email: string): Promise<void> => {
    try {
        const { error } = await supabase.auth.resetPasswordForEmail(email, {
            redirectTo: `${window.location.origin}/reset-password`
        });

        if (error) throw error;
    } catch (error) {
        handleServiceError('enviar redefinição de senha', error);
        throw error;
    }
};
